import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import {
  ANCHOR_MARGIN,
  buildGroups,
  detectScope,
  filterRankSuggestions,
  insertOpensNewContext,
  REFRESH_DEBOUNCE_MS,
  resolveSuggestions,
} from './utils';

/**
 * Controller for the custom autocomplete popover that replaces Monaco's native
 * suggest widget. Subscribes to editor content/cursor/scroll/key events, computes
 * grouped suggestions from the shared suggestion engine, anchors the popover at the
 * caret and inserts the picked item via executeEdits.
 *
 * @param {{
 *   editorRef: { current: Object },
 *   monacoRef: { current: Object },
 *   schemaParams: Object,
 *   isOnlyBooleanAllowed: boolean,
 *   isNewExpressionsParsingLogicEnabled: boolean,
 *   isReadOnly: boolean,
 *   isMounted: boolean,
 * }} params
 * @returns {{ state: Object|null, onHover: Function, onSelect: Function }}
 */
const useExpressionAutocomplete = ({
  editorRef,
  monacoRef,
  schemaParams,
  isOnlyBooleanAllowed,
  isNewExpressionsParsingLogicEnabled,
  isReadOnly,
  isMounted,
}) => {
  const [state, setState] = useState(null);
  const stateRef = useRef(null);
  // Cursor position left by the last leaf/value accept. While the caret still sits
  // there, suppress re-opening the popover (accepting fires onDidChangeModelContent,
  // which would otherwise immediately re-suggest the item the user just picked).
  const justAcceptedRef = useRef(null);

  stateRef.current = state;

  const close = useCallback(() => {
    setState(null);
  }, []);

  const computeAnchor = useCallback(
    position => {
      const editor = editorRef.current;
      const node = editor?.getDomNode?.();

      if (!editor || !node) {
        return { top: 0, left: 0 };
      }

      const visible = editor.getScrolledVisiblePosition(position);

      if (!visible) {
        return { top: 0, left: 0 };
      }

      const width = node.clientWidth;
      const left = Math.max(0, Math.min(visible.left, width - 12));

      return { top: visible.top + visible.height + ANCHOR_MARGIN, left };
    },
    [editorRef],
  );

  const refresh = useCallback(() => {
    const editor = editorRef.current;

    if (!editor || isReadOnly) {
      setState(null);

      return;
    }

    const position = editor.getPosition();
    const model = editor.getModel();

    if (!position || !model) {
      setState(null);

      return;
    }

    const accepted = justAcceptedRef.current;

    if (
      accepted &&
      accepted.lineNumber === position.lineNumber &&
      accepted.column === position.column
    ) {
      // Caret hasn't moved since a leaf accept — keep the popover closed.
      setState(null);

      return;
    }

    justAcceptedRef.current = null;

    const textBeforeCursor = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });

    const editorValue = model.getValue();

    const { replaceLength, suggestions } = resolveSuggestions({
      textBeforeCursor,
      editorValue,
      schemaParams,
      isOnlyBooleanAllowed,
      isNewExpressionsParsingLogicEnabled,
    });

    const typed = textBeforeCursor.slice(textBeforeCursor.length - replaceLength);
    const ranked = filterRankSuggestions(suggestions, typed);

    if (!ranked.length) {
      setState(null);

      return;
    }

    setState({
      suggestions: ranked,
      replaceLength,
      typed,
      scope: detectScope(textBeforeCursor),
      caret: { line: position.lineNumber, column: position.column },
      anchor: computeAnchor(position),
      activeIndex: 0,
    });
  }, [
    editorRef,
    isReadOnly,
    schemaParams,
    isOnlyBooleanAllowed,
    isNewExpressionsParsingLogicEnabled,
    computeAnchor,
  ]);

  const insertAt = useCallback(
    index => {
      const { current: activeState } = stateRef;
      const editor = editorRef.current;
      const monaco = monacoRef.current;

      if (!activeState || !editor || !monaco) {
        return;
      }

      const suggestion = activeState.suggestions[index];

      if (!suggestion) {
        return;
      }

      const position = editor.getPosition();
      const range = new monaco.Range(
        position.lineNumber,
        position.column - activeState.replaceLength,
        position.lineNumber,
        position.column,
      );

      editor.executeEdits('autocomplete', [{ range, text: suggestion.insertText }]);

      const inserted = suggestion.insertText ?? '';
      const endColumn = position.column - activeState.replaceLength + inserted.length;

      editor.setPosition({ lineNumber: position.lineNumber, column: endColumn });
      editor.focus();
      setState(null);

      // An entity dot ('Path.'), function paren ('FN('), or operator (trailing space)
      // opens a new context — re-trigger to chain into it. A completed leaf/value ends
      // in a word character; there we remember the caret so the accept's own
      // content-change refresh doesn't re-open the popover on the item just picked.
      if (insertOpensNewContext(inserted)) {
        setTimeout(refresh, 0);
      } else {
        justAcceptedRef.current = { lineNumber: position.lineNumber, column: endColumn };
      }
    },
    [editorRef, monacoRef, refresh],
  );

  const moveActive = useCallback(delta => {
    setState(prev => {
      if (!prev) {
        return prev;
      }

      const count = prev.suggestions.length;
      const next = (prev.activeIndex + delta + count) % count;

      return { ...prev, activeIndex: next };
    });
  }, []);

  const onHover = useCallback(index => {
    setState(prev => (prev ? { ...prev, activeIndex: index } : prev));
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;

    if (!isMounted || !editor || !monaco) {
      return undefined;
    }

    const debouncedRefresh = _.debounce(refresh, REFRESH_DEBOUNCE_MS);

    const contentSub = editor.onDidChangeModelContent(() => {
      debouncedRefresh();
    });

    const cursorSub = editor.onDidChangeCursorPosition(event => {
      if (event.reason === monaco.editor.CursorChangeReason.Explicit && stateRef.current) {
        debouncedRefresh();
      }
    });

    const scrollSub = editor.onDidScrollChange(() => {
      if (!stateRef.current) {
        return;
      }

      const position = editor.getPosition();

      setState(prev => (prev ? { ...prev, anchor: computeAnchor(position) } : prev));
    });

    const blurSub = editor.onDidBlurEditorWidget(() => {
      setTimeout(() => setState(null), 120);
    });

    const keySub = editor.onKeyDown(event => {
      if (!stateRef.current) {
        return;
      }

      const { KeyCode } = monaco;

      if (event.keyCode === KeyCode.DownArrow) {
        event.preventDefault();
        event.stopPropagation();
        moveActive(1);

        return;
      }

      if (event.keyCode === KeyCode.UpArrow) {
        event.preventDefault();
        event.stopPropagation();
        moveActive(-1);

        return;
      }

      if (event.keyCode === KeyCode.Enter || event.keyCode === KeyCode.Tab) {
        event.preventDefault();
        event.stopPropagation();
        insertAt(stateRef.current.activeIndex);

        return;
      }

      if (event.keyCode === KeyCode.Escape) {
        event.preventDefault();
        event.stopPropagation();
        setState(null);
      }
    });

    return () => {
      debouncedRefresh.cancel();
      contentSub.dispose();
      cursorSub.dispose();
      scrollSub.dispose();
      blurSub.dispose();
      keySub.dispose();
    };
  }, [isMounted, editorRef, monacoRef, refresh, moveActive, insertAt, computeAnchor]);

  const groups = useMemo(
    () =>
      state
        ? buildGroups({ suggestions: state.suggestions, typed: state.typed, scope: state.scope })
        : [],
    [state],
  );

  const popoverState = useMemo(() => {
    if (!state) {
      return null;
    }

    return {
      groups,
      activeIndex: state.activeIndex,
      anchor: state.anchor,
      typed: state.typed,
      scope: state.scope,
      caret: state.caret,
    };
  }, [state, groups]);

  return { state: popoverState, onHover, onSelect: insertAt, close, trigger: refresh };
};

export default useExpressionAutocomplete;
